import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "@tanstack/react-router";
import { toggleFavorite } from "@/lib/account.functions";

type Kind = "music" | "song" | "wisdom" | "activation" | "book";

interface FavoriteButtonProps {
  kind: Kind;
  slug: string;
  className?: string;
}

/**
 * Heart toggle. Reads current favorite state directly from the database on
 * mount for the signed-in user. When signed out, renders as a "Sign in to save"
 * link — never redirects, never hidden.
 */
export function FavoriteButton({ kind, slug, className }: FavoriteButtonProps) {
  const toggle = useServerFn(toggleFavorite);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [favorited, setFavorited] = useState<boolean>(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data: userData } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!userData.user) {
        setAuthed(false);
        return;
      }
      setAuthed(true);
      const { data } = await supabase
        .from("user_favorites")
        .select("id")
        .eq("kind", kind)
        .eq("slug", slug)
        .maybeSingle();
      if (cancelled) return;
      setFavorited(Boolean(data));
    }
    void load();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") void load();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [kind, slug]);

  if (authed === false) {
    return (
      <Link
        to="/auth"
        className={
          className ??
          "inline-flex min-h-11 items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
        }
        aria-label="Sign in to save"
      >
        <Heart size={12} aria-hidden /> Sign in to save
      </Link>
    );
  }

  const handle = async () => {
    if (busy || !authed) return;
    setBusy(true);
    const previous = favorited;
    setFavorited(!previous);
    try {
      const res = await toggle({ data: { kind, slug } });
      setFavorited(res.favorited);
    } catch {
      setFavorited(previous);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy || authed === null}
      aria-pressed={favorited}
      aria-label={favorited ? "Remove from saved" : "Save"}
      className={
        className ??
        `inline-flex min-h-11 items-center gap-2 rounded-full border px-4 py-2 text-xs transition-colors ${
          favorited
            ? "border-gold bg-gold/10 text-gold-luminous"
            : "hairline text-muted-foreground hover:border-gold hover:text-gold"
        }`
      }
    >
      <Heart size={12} className={favorited ? "fill-current" : ""} aria-hidden />
      {favorited ? "Saved" : "Save"}
    </button>
  );
}
